import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Avatar from "shared/Avatar/Avatar";
import Badge from "shared/Badge/Badge";
import NcImage from "shared/NcImage/NcImage";
import SocialsList from "shared/SocialsList/SocialsList";
import { Helmet } from "react-helmet-async";
import { IContentPost, IPost } from "../../@core/interfaces/IPost";
import { useBlog } from "../../hooks/collections/useBlog";
import moment from "moment";
import QuillRenderer from "../../@core/components/QuillRenderer";

const BlogSingle = () => {
  const { postId } = useParams();
  const [post, setPost] = useState<IPost>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { getPost, posts } = useBlog({
    loadData: true,
    queries: [
      { type: "limit", value: 5 },
      { type: "orderBy", field: "createdAt", direction: "desc" },
    ]
  });

  const handleFetchPost = async () => {
    try {
      const loadedPost = await getPost(postId);
      if (!loadedPost) navigate("/blog");
      setPost(loadedPost);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleFetchPost();
  }, [postId]);

  if (isLoading) return <></>;

  const renderHeader = () => {
    return (
      <header className="container rounded-xl">
        <div className="max-w-screen-md mx-auto space-y-5">
          <Badge href="##" color="purple" name="New post"/>
          <h1
            className=" text-neutral-900 font-semibold text-3xl md:text-4xl md:!leading-[120%] lg:text-4xl dark:text-neutral-100 max-w-4xl "
            title={post.title}
          >
            {post.title}
          </h1>
          <span className="block text-base text-neutral-500 md:text-lg dark:text-neutral-400 pb-1">
            {post.description}
          </span>

          <div className="w-full border-b border-neutral-100 dark:border-neutral-800"></div>
          <div className="flex flex-col items-baseline sm:flex-row sm:justify-between">
            <div className="nc-PostMeta2 flex items-center flex-wrap text-neutral-700 text-left dark:text-neutral-200 text-sm leading-none flex-shrink-0">
              <Avatar
                imgUrl={post.company?.imageUrl}
                containerClassName="flex-shrink-0"
                sizeClass="w-8 h-8 sm:h-11 sm:w-11 "
              />
              <div className="ml-3">
                <div className="flex items-center">
                  <div className="block font-semibold">
                    {post.company.name}
                  </div>
                </div>
                <div className="text-xs mt-[6px]">
                  <span className="text-neutral-700 dark:text-neutral-300">
                   {moment(post.createdAt).format("MMM DD, YYYY")}
                  </span>
                  <span className="mx-2 font-semibold">·</span>
                  <span className="text-neutral-700 dark:text-neutral-300">
                    6 min read
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-3 sm:mt-0 sm:ml-3">
              <SocialsList/>
            </div>
          </div>
        </div>
      </header>
    );
  };

  const renderContent = (content: IContentPost) => {
    return (
      <div
        id="single-entry-content"
        className="prose dark:prose-invert prose-sm !max-w-screen-md sm:prose lg:prose-lg mx-auto dark:prose-dark"
      >
        <h3>{content.subtitle}</h3>
        <QuillRenderer content={content.content}/>
      </div>
    );
  };

  const renderPostRelated = (post: IPost) => {
    return (
      <div
        key={post.id}
        className="relative aspect-w-3 aspect-h-4 rounded-3xl overflow-hidden group"
      >
        <Link to={`/blog/${post.id}`}/>
        <NcImage
          className="object-cover w-full h-full transform group-hover:scale-105 transition-transform duration-300"
          src={post.coverURL}
        />
        <div>
          <div className="absolute bottom-0 inset-x-0 h-1/2 bg-gradient-to-t from-black"></div>
        </div>
        <div className="flex flex-col justify-end items-start text-xs text-neutral-300 space-y-2.5 p-4">
          <Badge name="Categories"/>
          <h2 className="block text-lg font-semibold text-white ">
            <span className="line-clamp-2">{post.title}</span>
          </h2>

          <div className="flex">
            <span className="block text-neutral-200 hover:text-white font-medium truncate">
              {post.company.name}
            </span>
            <span className="mx-1.5 font-medium">·</span>
            <span className="font-normal truncate">{moment(post.company.createdAt).format("MMM DD, YYYY")}</span>
          </div>
        </div>
        <Link to={`/blog/${post.id}`}/>
      </div>
    );
  };

  return (
    <div className="nc-PageSingle pt-8 lg:pt-16 ">
      <Helmet>
        <title>Single Blog</title>
      </Helmet>
      {renderHeader()}
      <NcImage
        className="w-full rounded-xl"
        containerClassName="container my-10 sm:my-12"
        src={post.coverURL}
      />

      <div className="nc-SingleContent container space-y-10">
        {post.contents.map((content) => renderContent(content))}
      </div>
      <div className="relative bg-neutral-100 dark:bg-neutral-800 py-16 lg:py-28 mt-16 lg:mt-24">
        <div className="container ">
          <h2 className="text-3xl font-semibold">Lastest posts</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
            {posts.filter(post => post.id !== postId).map((post) => renderPostRelated(post))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogSingle;
